// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ProgressBar, BadgeDisplay, StreakCounter, PremiumBanner, GlobalProgressBar } from '../components/UIComponents';
import { User, Mail, Calendar, Crown, LogOut, Award, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import CertificateModal from '../components/CertificateModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certModal, setCertModal] = useState({ open: false, level: null });

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/progress`, { credentials: 'include' });
        if (res.ok) setProgress(await res.json());
        const cRes = await fetch(`${API_URL}/api/certificates`, { credentials: 'include' });
        if (cRes.ok) setCertificates(await cRes.json());
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, []);

  const handleUpgrade = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/upgrade`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Upgrade a Premium effettuato!');
        refreshUser();
      }
    } catch (error) {
      toast.error('Errore durante l\'upgrade');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const allBadges = ['primo_passo', 'studente_base', 'investitore_junior', 'esperto_portafoglio', 'maestro_finanza', 'quiz_master', 'perfezionista', 'costante', 'maratoneta'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]">
        <div className="w-12 h-12 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="profile-page">
      <GlobalProgressBar value={progress?.overall?.percentage || 0} />
      
      <div className="container-wide max-w-4xl pt-2">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl font-medium text-[#E8E8E4] mb-4">Il mio profilo</h1>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="sapiens-card p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#252523] flex items-center justify-center">
                <User className="w-12 h-12 text-[#888780]" />
              </div>
            )}
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row items-center gap-3 mb-2">
                <h2 className="text-2xl font-medium text-[#E8E8E4]">{user?.name}</h2>
                {user?.tier === 'premium' && <span className="premium-badge flex items-center gap-1"><Crown className="w-3 h-3" />Premium</span>}
              </div>
              <div className="flex flex-col md:flex-row items-center gap-4 text-[#888780] mb-4">
                <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{user?.email}</span>
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />Membro dal {new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</span>
              </div>
              {progress && <StreakCounter streak={progress.streak} />}
            </div>

            <Button className="sapiens-btn-secondary flex items-center gap-2" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />Esci
            </Button>
          </div>
        </motion.div>

        {user?.tier !== 'premium' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
            <PremiumBanner onUpgrade={handleUpgrade} />
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="sapiens-card p-6">
            <h3 className="text-lg font-medium text-[#E8E8E4] mb-6">Progressi di apprendimento</h3>
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-[#888780]">Completamento totale</span>
                <span className="font-medium tabular-nums text-[#E8E8E4]">{progress?.overall?.percentage || 0}%</span>
              </div>
              <ProgressBar value={progress?.overall?.percentage || 0} size="large" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#1C1C1A] rounded-xl text-center">
                <p className="text-3xl font-medium tabular-nums text-[#3D7A52]">{progress?.overall?.completed || 0}</p>
                <p className="text-sm text-[#888780]">Lezioni completate</p>
              </div>
              <div className="p-4 bg-[#1C1C1A] rounded-xl text-center">
                <p className="text-3xl font-medium tabular-nums text-[#3D7A52]">{Object.keys(progress?.quiz_scores || {}).length}</p>
                <p className="text-sm text-[#888780]">Quiz completati</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="sapiens-card p-6">
            <h3 className="text-lg font-medium text-[#E8E8E4] mb-6">Punteggi quiz</h3>
            {Object.keys(progress?.quiz_scores || {}).length === 0 ? (
              <p className="text-[#888780] text-center py-8">Non hai ancora completato nessun quiz</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {Object.entries(progress?.quiz_scores || {}).map(([lessonId, score]) => (
                  <div key={lessonId} className="flex items-center justify-between p-3 bg-[#1C1C1A] rounded-lg">
                    <span className="text-sm text-[#E8E8E4] truncate">{lessonId.replace('lesson_', 'Lezione ')}</span>
                    <span className={`font-medium tabular-nums ${score >= 80 ? 'text-[#3D7A52]' : score >= 60 ? 'text-[#C9A84C]' : 'text-[#F09595]'}`}>{score}%</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="sapiens-card p-6">
          <h3 className="text-lg font-medium text-[#E8E8E4] mb-6">Badge guadagnati ({progress?.badges?.length || 0}/{allBadges.length})</h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {allBadges.map((badge) => <BadgeDisplay key={badge} badge={badge} earned={progress?.badges?.includes(badge)} />)}
          </div>
        </motion.div>

        {/* Certificates Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mt-8 sapiens-card p-6" data-testid="certificates-section">
          <div className="flex items-center gap-3 mb-6">
            <Award className="w-6 h-6 text-[#C9A84C]" />
            <h3 className="text-lg font-medium text-[#E8E8E4]">Certificati di completamento</h3>
          </div>
          {certificates.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 rounded-full bg-[#252523] flex items-center justify-center mx-auto mb-4">
                <Award className="w-7 h-7 text-[#888780]" />
              </div>
              <p className="text-[#888780] text-sm">
                {user?.tier === 'premium'
                  ? 'Completa un livello per ottenere il tuo certificato'
                  : 'Completa le lezioni per sbloccare i certificati con Premium'}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {certificates.map((cert) => (
                <div key={cert.certificate_id} className="p-4 bg-[#1C1C1A] rounded-xl border border-[#C9A84C]/20 flex items-center justify-between" data-testid={`profile-cert-${cert.level}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
                      <Award className="w-5 h-5 text-[#C9A84C]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#E8E8E4]">{cert.level_name}</p>
                      <p className="text-xs text-[#888780]">
                        {new Date(cert.issued_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCertModal({ open: true, level: cert.level, levelName: cert.level_name })}
                    className="text-xs text-[#C9A84C] hover:text-[#E8E8E4] transition-colors font-medium px-3 py-1.5 rounded-lg border border-[#C9A84C]/30 hover:bg-[#C9A84C]/10"
                    data-testid={`view-cert-${cert.level}`}
                  >
                    Visualizza
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Free user teaser */}
          {user?.tier !== 'premium' && (
            <div className="mt-4 p-4 rounded-xl border border-[#C9A84C]/20 bg-[#C9A84C]/5 flex items-center gap-3">
              <Lock className="w-4 h-4 text-[#C9A84C] shrink-0" />
              <p className="text-sm text-[#888780]">
                <span className="text-[#C9A84C] font-medium">Premium</span>: ottieni certificati scaricabili in PDF al completamento di ogni livello
              </p>
            </div>
          )}
        </motion.div>

        {user?.tier === 'premium' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8 sapiens-card p-6 border-[#C9A84C]/30">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-6 h-6 text-[#C9A84C]" />
              <h3 className="text-lg font-medium text-[#E8E8E4]">Vantaggi Premium</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-[#E8E8E4]">
              <div className="flex items-center gap-2"><span className="text-[#3D7A52]">✓</span>Tutti i livelli sbloccati</div>
              <div className="flex items-center gap-2"><span className="text-[#3D7A52]">✓</span>Quiz illimitati</div>
              <div className="flex items-center gap-2"><span className="text-[#3D7A52]">✓</span>Tutti i calcolatori</div>
              <div className="flex items-center gap-2"><span className="text-[#3D7A52]">✓</span>Certificati di completamento</div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Certificate Modal */}
      <CertificateModal
        isOpen={certModal.open}
        onClose={() => setCertModal({ open: false, level: null })}
        level={certModal.level}
        levelName={certModal.levelName}
        userName={user?.name}
        isPremium={user?.tier === 'premium'}
        issuedAt={certificates.find(c => c.level === certModal.level)?.issued_at}
        onDownload={() => {}}
      />
    </div>
  );
};

export default ProfilePage;
